import {
  DOCCHAT_SELECTION_BAR_ATTRIBUTE,
  type DocChatSelectedQuote,
  readDocChatSelection,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatSelection'
import { type RefObject, useCallback, useEffect, useState } from 'react'

/**
 * Tracks the text selected inside `containerRef`, for as long as `enabled`.
 * Returns the selection and a `clear` that drops it and the browser selection.
 */
export function useDocChatSelection(
  containerRef: RefObject<HTMLElement | null>,
  enabled: boolean
): [DocChatSelectedQuote | null, () => void] {
  const [quote, setQuote] = useState<DocChatSelectedQuote | null>(null)

  const clear = useCallback(() => {
    setQuote(null)
    window.getSelection()?.removeAllRanges()
  }, [])

  useEffect(() => {
    if (!enabled) return

    const updateFromSelection = (event: Event) => {
      if (isInsideSelectionBar(event.target)) return
      const container = containerRef.current
      setQuote(
        container
          ? readDocChatSelection(window.getSelection(), (node) =>
              node instanceof Node ? container.contains(node) : false
            )
          : null
      )
    }
    const clearOnScroll = () => setQuote(null)
    const clearOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setQuote(null)
    }

    document.addEventListener('mouseup', updateFromSelection)
    document.addEventListener('keyup', updateFromSelection)
    document.addEventListener('keydown', clearOnEscape)
    window.addEventListener('scroll', clearOnScroll, true)
    window.addEventListener('resize', clearOnScroll)
    return () => {
      document.removeEventListener('mouseup', updateFromSelection)
      document.removeEventListener('keyup', updateFromSelection)
      document.removeEventListener('keydown', clearOnEscape)
      window.removeEventListener('scroll', clearOnScroll, true)
      window.removeEventListener('resize', clearOnScroll)
      setQuote(null)
    }
  }, [containerRef, enabled])

  return [quote, clear]
}

function isInsideSelectionBar(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(`[${DOCCHAT_SELECTION_BAR_ATTRIBUTE}]`) !== null
  )
}
